import { getColorScheme, type ColorScheme } from '../../theme/colorSchemes';

export interface Tab {
  key: string;
  label: string;
  count?: number;
}

export interface TabNavProps {
  tabs: Tab[];
  activeKey: string;
  onTabChange: (key: string) => void;
  colorScheme?: ColorScheme;
  variant?: 'default' | 'compact' | 'pills';
}

/**
 * Reusable tab navigation component with count badges.
 * Extracted from ArtifactList to enable reuse across the application.
 * 
 * Features:
 * - Themeable color scheme (blue/teal)
 * - Count badges with dynamic styling
 * - Multiple variants: underline (default/compact) and pills
 * - Active state with border indicator or solid background
 */
export function TabNav({ 
  tabs, 
  activeKey, 
  onTabChange, 
  colorScheme = 'blue',
  variant = 'default' 
}: TabNavProps) {
  const colors = getColorScheme(colorScheme);
  
  // Pills variant uses a different layout
  if (variant === 'pills') {
    return (
      <nav className="flex items-center gap-1" aria-label="Primary">
        {tabs.map(({ key, label, count }) => {
          const isActive = activeKey === key;
          const base = 'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors';
          const tone = isActive ? colors.tabPillsActive : colors.tabPillsInactive;
          
          return (
            <button
              key={key}
              onClick={() => onTabChange(key)}
              className={`${base} ${tone}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <span>{label}</span>
              {typeof count === 'number' && (
                <span className={`inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs ${
                  isActive ? colors.tabPillsActiveCount : colors.tabPillsInactiveCount
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    );
  }
  
  // Default and compact variants use underline style
  const sizeClasses = variant === 'compact' 
    ? 'px-2.5 py-2 text-xs'
    : 'px-4 py-2.5 text-sm';

  return (
    <div className="flex-none border-b border-border-secondary">
      <nav className="flex px-4">
        {tabs.map(({ key, label, count }) => {
          const isActive = activeKey === key;
          
          return (
            <button
              key={key}
              onClick={() => onTabChange(key)}
              className={`
                ${sizeClasses}
                font-medium border-b-2 -mb-px transition-colors
                ${isActive ? colors.tabActive : colors.tabInactive}
              `}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className={`ml-1 ${isActive ? colors.tabActiveCount : colors.tabInactiveCount}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
